// ================================================
// SellerCreateProductForm.js  (UNIFIED WITH ADMIN UX)
// ================================================
import React, { useEffect, useState } from "react";
import "../ProductManagement/Products.css";
import config from "../../config";
import { getAuth, signInAnonymously } from "firebase/auth";
import { storage } from "../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const RequiredStar = () => (
  <span style={{ color: "#dc2626", marginLeft: 4 }}>*</span>
);

/* ===========================
   IMAGE NORMALIZATION HELPERS
   (Phase 1: smart top-crop 3:4)
   =========================== */

const IMAGE_CONFIG = {
  aspectRatio: 3 / 4,
  maxWidth: 1200,
  hardMin: 400,
  quality: 0.9,
};

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function normalizeImageFile(file) {
  const img = await loadImageFromFile(file);

  const { aspectRatio, maxWidth, hardMin, quality } = IMAGE_CONFIG;

  const originalWidth = img.naturalWidth;
  const originalHeight = img.naturalHeight;

  if (originalWidth < hardMin || originalHeight < hardMin) {
    throw new Error(
      `Image is too small (${originalWidth}×${originalHeight}). Please upload a higher resolution image.`
    );
  }

  let targetWidth = Math.min(originalWidth, maxWidth);
  let targetHeight = Math.round(targetWidth / aspectRatio);

  if (targetHeight > originalHeight) {
    targetHeight = originalHeight;
    targetWidth = Math.round(targetHeight * aspectRatio);
  }

  const cropWidth = targetWidth;
  const cropHeight = targetHeight;

  let cropX = Math.round((originalWidth - cropWidth) / 2);
  let cropY;
  const ratio = originalWidth / originalHeight;

  if (ratio >= aspectRatio) {
    // wide / square – keep from TOP to avoid cutting head
    cropY = 0;
  } else {
    // tall – center with slight upward bias
    cropY = Math.round((originalHeight - cropHeight) / 2);
    const upwardBias = Math.round(cropHeight * 0.05);
    cropY = Math.max(0, cropY - upwardBias);
    if (cropY + cropHeight > originalHeight) {
      cropY = originalHeight - cropHeight;
    }
  }

  if (cropX < 0) cropX = 0;
  if (cropX + cropWidth > originalWidth) {
    cropX = originalWidth - cropWidth;
  }
  if (cropY < 0) cropY = 0;
  if (cropY + cropHeight > originalHeight) {
    cropY = originalHeight - cropHeight;
  }

  const canvas = document.createElement("canvas");
  canvas.width = cropWidth;
  canvas.height = cropHeight;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    img,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight
  );

  const blob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", quality)
  );

  const normalizedFile = new File(
    [blob],
    file.name.replace(/\.(png|jpe?g|webp)$/i, "") + "_aurakart.jpg",
    { type: "image/jpeg" }
  );

  return {
    normalizedFile,
    normalizedWidth: cropWidth,
    normalizedHeight: cropHeight,
    originalWidth,
    originalHeight,
  };
}

// quick warnings just for seller preview (lighter than admin)
async function getPreviewInfo(file) {
  const img = await loadImageFromFile(file);
  const ow = img.naturalWidth;
  const oh = img.naturalHeight;

  const warnings = [];
  if (ow < 800 || oh < 1000) {
    warnings.push("Low resolution – may look blurred on zoom.");
  }
  const ratio = ow / oh;
  if (ratio > 1) warnings.push("Wide image – top area will be prioritised.");
  if (ratio < 0.7) warnings.push("Very tall image – sides may be cropped.");

  return {
    previewUrl: URL.createObjectURL(file),
    warnings,
    originalWidth: ow,
    originalHeight: oh,
  };
}

/* ================================================
   MAIN COMPONENT
   ================================================ */

export default function SellerCreateProductForm({
  sellerId,
  onClose,
  onSuccess,
}) {
  const [currentStep, setCurrentStep] = useState(1);

  // color list used on variants + image-color dropdown
  const colors = [
    "Red",
    "Blue",
    "Green",
    "Black",
    "White",
    "Yellow",
    "Orange",
    "Pink",
    "Purple",
    "Brown",
    "Gray",
    "Navy",
    "Maroon",
    "Dark Blue",
    "Light Blue",
    "Beige",
    "Cream",
    "Gold",
    "Silver",
    "Olive",
  ];

  // simple static categories (your backend will create if missing)
  const categories = ["Boys", "Girls", "Ladies", "Men", "Women"];
  const subCategories = {
    Boys: [
      "Shirts",
      "T-Shirts",
      "Pants",
      "Shorts",
      "Jackets",
      "Shoes",
      "Accessories",
    ],
    Girls: [
      "Dresses",
      "Tops",
      "Skirts",
      "Pants",
      "Jackets",
      "Shoes",
      "Accessories",
    ],
    Ladies: [
      "Kurtis",
      "Sarees",
      "Tops",
      "Dresses",
      "Pants",
      "Jackets",
      "Shoes",
      "Accessories",
    ],
    Men: [
      "Shirts",
      "T-Shirts",
      "Pants",
      "Jeans",
      "Jackets",
      "Shoes",
      "Accessories",
    ],
    Women: ["Sarees", "Kurtis", "Dresses", "Tops", "Bottoms", "Lehengas"],
  };

  // sizes from DB (same logic as Admin)
  const [allSizes, setAllSizes] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [sizesLoading, setSizesLoading] = useState(false);
  const [sizesError, setSizesError] = useState("");

  const sizeOptions = availableSizes.length > 0 ? availableSizes : allSizes;

  const [form, setForm] = useState({
    productName: "",
    productDescription: "",
    categoryName: "",
    subCategoryName: "",
    hsnCode: "", // ✅ HSN code for seller product creation
    details: {
      fabric: "",
      pattern: "",
      neck: "",
      sleeveLength: "",
      fit: "",
      occasion: "",
      brand: "",
      countryOfOrigin: "India",
      material: "",
      careInstructions: "",
      features: "",
      season: "",
      closure: "",
      manufacturer: "",
    },
    variants: [
      {
        sku: "",
        sizeName: "",
        colorName: "",
        originalPrice: "",
        discount: "",
        offerPrice: "",
        stockQuantity: "",
      },
    ],
  });

  const [productId, setProductId] = useState(null);

  // colors available on this product (from variants) – same endpoint admin uses
  const [productColors, setProductColors] = useState([]);
  const [selectedImageColor, setSelectedImageColor] = useState("");
  const [completedColors, setCompletedColors] = useState([]);

  // image state: for each view we keep normalized file + preview info
  const [imageViewTypes, setImageViewTypes] = useState({
    "Main View": null,
    "Front View": null,
    "Back View": null,
    "Left Side View": null,
    "Right Side View": null,
  });
  const [imagePreviewData, setImagePreviewData] = useState({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* ===========================
     FETCH SIZES
     =========================== */

  // all sizes fallback
  useEffect(() => {
    const fetchAllSizes = async () => {
      try {
        const res = await fetch(`${config.baseUrl}/api/sizes`);
        if (res.ok) {
          let data = await res.json();
          data = (data || []).sort(
            (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
          );
          setAllSizes(data);
        }
      } catch (err) {
        console.error("Failed to fetch all sizes", err);
      }
    };
    fetchAllSizes();
  }, []);

  // subCategory-specific sizes
  useEffect(() => {
    if (!form.categoryName || !form.subCategoryName) {
      setAvailableSizes([]);
      setSizesError("");
      return;
    }

    const fetchSizesForSubCategory = async () => {
      setSizesLoading(true);
      setSizesError("");
      try {
        const params = new URLSearchParams({
          categoryName: form.categoryName,
          subCategoryName: form.subCategoryName,
        });
        const res = await fetch(
          `${config.baseUrl}/api/subCategorySizes?${params.toString()}`
        );
        if (res.ok) {
          let data = await res.json();
          data = (data || []).sort(
            (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
          );
          setAvailableSizes(data);
          if (!data || data.length === 0) {
            setSizesError(
              "No specific sizes configured for this sub-category. Showing all sizes as fallback."
            );
          }
        } else {
          setAvailableSizes([]);
          setSizesError(
            "Unable to load sizes for this sub-category. Showing all sizes as fallback."
          );
        }
      } catch (err) {
        console.error("Failed to fetch subCategory sizes:", err);
        setAvailableSizes([]);
        setSizesError(
          "Failed to load sub-category sizes. Showing all sizes as fallback."
        );
      } finally {
        setSizesLoading(false);
      }
    };

    // reset existing size selection when category/subcategory changes
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((v) => ({ ...v, sizeName: "" })),
    }));

    fetchSizesForSubCategory();
  }, [form.categoryName, form.subCategoryName]);

  /* ===========================
     FETCH PRODUCT COLORS AFTER CREATE
     =========================== */
  useEffect(() => {
    if (!productId) return;

    const fetchProductColors = async () => {
      try {
        const res = await fetch(
          `${config.baseUrl}/api/products/${productId}/colors`
        );
        if (!res.ok) return;
        const data = await res.json();
        setProductColors(data || []);
        if (data && data.length > 0 && !selectedImageColor) {
          setSelectedImageColor(data[0].colorName);
        }
      } catch (err) {
        console.error("Failed to fetch product colors:", err);
      }
    };

    fetchProductColors();
  }, [productId, selectedImageColor]);

  /* ===========================
     BASIC HANDLERS
     =========================== */

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDetailsChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      details: { ...prev.details, [field]: value },
    }));
  };

  const handleVariantChange = (index, field, value) => {
    const variants = [...form.variants];
    variants[index] = { ...variants[index], [field]: value };

    if (field === "discount" || field === "originalPrice") {
      const op = parseFloat(variants[index].originalPrice) || 0;
      const d = parseFloat(variants[index].discount) || 0;
      variants[index].offerPrice = (op * (1 - d / 100)).toFixed(2);
    }

    setForm((prev) => ({ ...prev, variants }));
  };

  const addVariant = (e) => {
    if (e) e.preventDefault();
    setForm((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          sku: "",
          sizeName: "",
          colorName: "",
          originalPrice: "",
          discount: "",
          offerPrice: "",
          stockQuantity: "",
        },
      ],
    }));
  };

  const removeVariant = (index) => {
    if (form.variants.length === 1) return;
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  // clone same as admin: copy, but clear SKU + stock
  const cloneVariant = (index) => {
    const src = form.variants[index];
    const cloned = {
      ...src,
      sku: "",
      stockQuantity: "",
    };
    const variants = [...form.variants];
    variants.splice(index + 1, 0, cloned);
    setForm((prev) => ({ ...prev, variants }));
  };

  /* ===========================
     IMAGES
     =========================== */

  const handleImageUploadForView = async (viewType, file) => {
    if (!file) return;
    try {
      const normalized = await normalizeImageFile(file);
      const preview = await getPreviewInfo(file);

      setImageViewTypes((prev) => ({
        ...prev,
        [viewType]: {
          file: normalized.normalizedFile,
          originalWidth: normalized.originalWidth,
          originalHeight: normalized.originalHeight,
          normalizedWidth: normalized.normalizedWidth,
          normalizedHeight: normalized.normalizedHeight, // ✅ fixed
        },
      }));

      setImagePreviewData((prev) => ({
        ...prev,
        [viewType]: {
          previewUrl: preview.previewUrl,
          warnings: preview.warnings,
          originalWidth: preview.originalWidth,
          originalHeight: preview.originalHeight,
        },
      }));
    } catch (err) {
      console.error("Image processing failed:", err);
      setError(
        err.message ||
          "Image processing failed. Please try with a higher resolution image."
      );
    }
  };

  const removeImageFromView = (viewType) => {
    setImageViewTypes((prev) => ({ ...prev, [viewType]: null }));
    setImagePreviewData((prev) => {
      const copy = { ...prev };
      delete copy[viewType];
      return copy;
    });
  };

  /* ===========================
     STEP 3: SAVE PRODUCT (NO IMAGES)
     =========================== */

  const handleSaveProductAtStep3 = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      if (!sellerId) {
        throw new Error("Seller ID is missing. Please re-login and try again.");
      }

      if (!form.productName || !form.categoryName || !form.subCategoryName) {
        throw new Error(
          "Please fill all mandatory fields: Product Name, Category and Sub Category."
        );
      }

      // ✅ HSN validation for seller product creation
      if (!form.hsnCode) {
        throw new Error("Please enter HSN Code.");
      }

      const filledVariants = form.variants.filter(
        (v) =>
          v.sizeName &&
          v.colorName &&
          v.sku &&
          v.originalPrice !== "" &&
          v.stockQuantity !== ""
      );

      if (filledVariants.length === 0) {
        throw new Error(
          "Please add at least one variant with Size, Color, SKU, Original Price and Stock Quantity."
        );
      }

      if (filledVariants.length !== form.variants.length) {
        throw new Error(
          "Please complete all variant fields (Size, Color, SKU, Original Price, Stock Quantity) or remove unfinished variants."
        );
      }

      const payload = {
        productName: form.productName,
        productDescription: form.productDescription,
        // product-level color removed for seller – colors live on variants
        color: null,
        categoryName: form.categoryName,
        subCategoryName: form.subCategoryName,
        hsnCode: form.hsnCode, // ✅ send HSN to backend
        active: false, // seller products are always inactive initially
        details: { ...form.details },
        variants: filledVariants.map((v) => ({
          sku: v.sku,
          sizeName: v.sizeName,
          // send both in case backend is using either field name
          colorName: v.colorName,
          color: v.colorName,
          originalPrice: parseFloat(v.originalPrice),
          offerPrice: parseFloat(v.offerPrice || 0),
          discount: parseFloat(v.discount || 0),
          stockQuantity: parseInt(v.stockQuantity || 0, 10),
        })),
        images: [],
        vendorId: null,
      };

      const res = await fetch(
        `${config.baseUrl}/api/seller/products/${sellerId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(
          errData.message || "Failed to create product for seller."
        );
      }

      const created = await res.json();
      const id = created.productId || created.id;
      if (!id) {
        throw new Error(
          "Product created but no product ID returned from server."
        );
      }

      setProductId(id);
      setSuccess(
        "Product saved successfully! Now upload images for each color in Step 4."
      );
      setCurrentStep(4);
    } catch (err) {
      setError(err.message || "Error while saving product");
    } finally {
      setLoading(false);
    }
  };

  /* ===========================
     STEP 4: SAVE IMAGES (PER COLOR)
     =========================== */

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      if (!productId) {
        throw new Error("Product ID missing. Please complete Step 3 first.");
      }

      if (productColors.length > 0 && !selectedImageColor) {
        throw new Error("Please select a color for these images.");
      }

      const allImages = Object.entries(imageViewTypes)
        .filter(([, info]) => info && info.file)
        .map(([viewType, info]) => ({ viewType, file: info.file }));

      if (allImages.length === 0) {
        throw new Error(
          "Please upload at least one image for the selected color."
        );
      }

      const auth = getAuth();
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }

      const uploadedImages = await Promise.all(
        allImages.map(async ({ viewType, file }, idx) => {
          const ts = Date.now();
          const fileName = `seller_${sellerId}_${productId}_${ts}_${idx}.jpg`;
          const storageRef = ref(
            storage,
            `seller-products/${sellerId}/${productId}/${fileName}`
          );

          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);

          return {
            imageUrl: url,
            imageType: viewType,
            displayOrder: idx + 1,
            color: selectedImageColor || null,
          };
        })
      );

      const imgRes = await fetch(
        `${config.baseUrl}/api/productimages/product/${productId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(uploadedImages),
        }
      );

      if (!imgRes.ok) {
        const errData = await imgRes.json().catch(() => ({}));
        console.warn(
          "Images uploaded to Firebase, but backend image save failed:",
          errData
        );
      }

      const label = selectedImageColor || "Default";

      if (label && !completedColors.includes(label)) {
        setCompletedColors((prev) => [...prev, label]);
      }

      setSuccess(
        `Images uploaded successfully for color: ${label}. You can switch to another color and upload again, or click "Finish Product & Close".`
      );

      setImageViewTypes({
        "Main View": null,
        "Front View": null,
        "Back View": null,
        "Left Side View": null,
        "Right Side View": null,
      });
      setImagePreviewData({});
    } catch (err) {
      setError(err.message || "Error during image upload");
    } finally {
      setLoading(false);
    }
  };

  const goToPrevStep = () =>
    setCurrentStep((prev) => Math.max(1, prev - 1));
  const goToNextStep = () =>
    setCurrentStep((prev) => Math.min(4, prev + 1));

  const handleFinish = () => {
    onSuccess && onSuccess();
    onClose && onClose();
  };

  /* ===========================
     RENDER
     =========================== */

  return (
    <div className="modal-overlay">
      <form className="modal-content large-modal" onSubmit={handleSubmit}>
        <div className="modal-header">
          <h2>Create Product (Seller)</h2>
          <div className="step-indicator">
            {[1, 2, 3, 4].map((step) => (
              <span
                key={step}
                className={
                  currentStep === step
                    ? "active"
                    : currentStep > step
                    ? "completed"
                    : ""
                }
                onClick={() => setCurrentStep(step)}
                title={["Basic Info", "Details", "Variants", "Images"][step - 1]}
              >
                {step}
              </span>
            ))}
          </div>
          <button type="button" className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {/* STEP 1: BASIC INFO */}
          {currentStep === 1 && (
            <div className="form-section">
              <h3>Step 1: Basic Product Information</h3>
              <p className="step-description">
                Start with name, category and description. Colors will be defined on
                each variant (size) in the next step.
              </p>

              <div className="form-grid">
                <div className="form-group">
                  <label>
                    Product Name <RequiredStar />
                  </label>
                  <input
                    value={form.productName}
                    onChange={(e) => handleChange("productName", e.target.value)}
                    placeholder="e.g., Kancheepuram Silk Saree – Royal Blue"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Category <RequiredStar />
                  </label>
                  <select
                    value={form.categoryName}
                    onChange={(e) => handleChange("categoryName", e.target.value)}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Sub Category <RequiredStar />
                  </label>
                  <select
                    value={form.subCategoryName}
                    onChange={(e) =>
                      handleChange("subCategoryName", e.target.value)
                    }
                    disabled={!form.categoryName}
                  >
                    <option value="">Select Sub Category</option>
                    {form.categoryName &&
                      subCategories[form.categoryName]?.map((sub) => (
                        <option key={sub} value={sub}>
                          {sub}
                        </option>
                      ))}
                  </select>
                </div>

                {/* HSN Code */}
                <div className="form-group">
                  <label>
                    HSN Code <RequiredStar />
                  </label>
                  <input
                    value={form.hsnCode}
                    onChange={(e) => handleChange("hsnCode", e.target.value)}
                    placeholder="Enter HSN Code"
                  />
                  <small className="helper-text">
                    Example: 6109 for T-Shirts, 6204 for dresses. Check with your CA
                    if unsure.
                  </small>
                </div>

                <div className="form-group full-width">
                  <label>Product Description</label>
                  <textarea
                    rows="4"
                    value={form.productDescription}
                    onChange={(e) =>
                      handleChange("productDescription", e.target.value)
                    }
                    placeholder="Describe fabric, work, fitting, special features, etc."
                  />
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={false} disabled />
                    <span className="checkbox-text">
                      Product will be created as <b>Inactive</b> and needs admin
                      approval before going live.
                    </span>
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={goToNextStep}
                  className="btn-primary"
                >
                  Next: Additional Details
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: ADDITIONAL DETAILS */}
          {currentStep === 2 && (
            <div className="form-section">
              <h3>Step 2: Additional Product Details</h3>
              <p className="step-description">
                These fields help customers understand the fabric, fit and usage.
                They are optional but recommended.
              </p>

              <div className="form-grid">
                <div className="form-group">
                  <label>Fabric</label>
                  <input
                    value={form.details.fabric}
                    onChange={(e) =>
                      handleDetailsChange("fabric", e.target.value)
                    }
                    placeholder="e.g., 100% Cotton"
                  />
                </div>

                <div className="form-group">
                  <label>Pattern</label>
                  <input
                    value={form.details.pattern}
                    onChange={(e) =>
                      handleDetailsChange("pattern", e.target.value)
                    }
                    placeholder="e.g., Solid, Striped"
                  />
                </div>

                <div className="form-group">
                  <label>Neck Type</label>
                  <input
                    value={form.details.neck}
                    onChange={(e) => handleDetailsChange("neck", e.target.value)}
                    placeholder="e.g., Round, V-neck, Collar"
                  />
                </div>

                <div className="form-group">
                  <label>Sleeve Length</label>
                  <select
                    value={form.details.sleeveLength}
                    onChange={(e) =>
                      handleDetailsChange("sleeveLength", e.target.value)
                    }
                  >
                    <option value="">Select Sleeve Length</option>
                    <option value="Sleeveless">Sleeveless</option>
                    <option value="Short Sleeve">Short Sleeve</option>
                    <option value="3/4 Sleeve">3/4 Sleeve</option>
                    <option value="Long Sleeve">Long Sleeve</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Fit</label>
                  <select
                    value={form.details.fit}
                    onChange={(e) => handleDetailsChange("fit", e.target.value)}
                  >
                    <option value="">Select Fit</option>
                    <option value="Loose">Loose</option>
                    <option value="Regular Fit">Regular Fit</option>
                    <option value="Slim Fit">Slim Fit</option>
                    <option value="Skinny">Skinny</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Occasion</label>
                  <input
                    value={form.details.occasion}
                    onChange={(e) =>
                      handleDetailsChange("occasion", e.target.value)
                    }
                    placeholder="e.g., Casual, Formal, Party"
                  />
                </div>

                <div className="form-group">
                  <label>Brand</label>
                  <input
                    value={form.details.brand}
                    onChange={(e) =>
                      handleDetailsChange("brand", e.target.value)
                    }
                    placeholder="Enter brand name"
                  />
                </div>

                <div className="form-group">
                  <label>Country of Origin</label>
                  <input
                    value={form.details.countryOfOrigin}
                    onChange={(e) =>
                      handleDetailsChange("countryOfOrigin", e.target.value)
                    }
                    placeholder="India"
                  />
                </div>

                <div className="form-group">
                  <label>Material</label>
                  <input
                    value={form.details.material}
                    onChange={(e) =>
                      handleDetailsChange("material", e.target.value)
                    }
                    placeholder="e.g., Cotton, Polyester"
                  />
                </div>

                <div className="form-group">
                  <label>Care Instructions</label>
                  <input
                    value={form.details.careInstructions}
                    onChange={(e) =>
                      handleDetailsChange("careInstructions", e.target.value)
                    }
                    placeholder="e.g., Machine wash cold"
                  />
                </div>

                <div className="form-group">
                  <label>Features</label>
                  <input
                    value={form.details.features}
                    onChange={(e) =>
                      handleDetailsChange("features", e.target.value)
                    }
                    placeholder="e.g., Button closure, Pockets"
                  />
                </div>

                <div className="form-group">
                  <label>Season</label>
                  <input
                    value={form.details.season}
                    onChange={(e) =>
                      handleDetailsChange("season", e.target.value)
                    }
                    placeholder="e.g., Spring, Summer, Winter"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="button"
                  onClick={goToNextStep}
                  className="btn-primary"
                >
                  Next: Variants &amp; Pricing
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: VARIANTS */}
          {currentStep === 3 && (
            <div className="form-section">
              <h3>Step 3: Variants, Colors &amp; Pricing</h3>
              <p className="step-description">
                Sizes are loaded based on Category and Sub Category using your
                <b> sub_category_sizes</b> table. Use the <b>Clone</b> button to
                quickly create similar variants (change only color / size / price).
              </p>

              {sizesLoading && (
                <div
                  className="error-message"
                  style={{
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    borderColor: "#bfdbfe",
                  }}
                >
                  Loading sizes for this sub-category…
                </div>
              )}
              {sizesError && (
                <div className="error-message" style={{ marginBottom: 10 }}>
                  {sizesError}
                </div>
              )}

              <div className="variants-container">
                {form.variants.map((variant, idx) => (
                  <div key={idx} className="variant-card">
                    <div className="variant-header">
                      <h5>Variant {idx + 1}</h5>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          style={{
                            background: "#e5e7eb",
                            borderRadius: 999,
                            border: "1px solid #d1d5db",
                            padding: "4px 10px",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                          onClick={() => cloneVariant(idx)}
                        >
                          Clone
                        </button>
                        {form.variants.length > 1 && (
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => removeVariant(idx)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="variant-grid">
                      <div className="form-group">
                        <label>
                          Size <RequiredStar />
                        </label>
                        <select
                          value={variant.sizeName}
                          onChange={(e) =>
                            handleVariantChange(idx, "sizeName", e.target.value)
                          }
                          disabled={sizeOptions.length === 0}
                        >
                          <option value="">Select Size</option>
                          {sizeOptions.map((size) => (
                            <option key={size.sizeId} value={size.code}>
                              {size.code}
                              {size.name ? ` - ${size.name}` : ""}
                            </option>
                          ))}
                        </select>
                        <small className="helper-text">
                          Sizes follow your mapping for this Category &amp; Sub
                          Category.
                        </small>
                      </div>

                      <div className="form-group">
                        <label>
                          Color <RequiredStar />
                        </label>
                        <select
                          value={variant.colorName}
                          onChange={(e) =>
                            handleVariantChange(idx, "colorName", e.target.value)
                          }
                        >
                          <option value="">Select Color</option>
                          {colors.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>
                          SKU <RequiredStar />
                        </label>
                        <input
                          value={variant.sku}
                          onChange={(e) =>
                            handleVariantChange(idx, "sku", e.target.value)
                          }
                          placeholder="Unique SKU code"
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          Original Price <RequiredStar />
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={variant.originalPrice}
                          onChange={(e) =>
                            handleVariantChange(
                              idx,
                              "originalPrice",
                              e.target.value
                            )
                          }
                          placeholder="0.00"
                        />
                      </div>

                      <div className="form-group">
                        <label>Discount (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={variant.discount}
                          onChange={(e) =>
                            handleVariantChange(idx, "discount", e.target.value)
                          }
                          placeholder="0"
                        />
                      </div>

                      <div className="form-group">
                        <label>Offer Price</label>
                        <input
                          type="number"
                          step="0.01"
                          value={variant.offerPrice}
                          readOnly
                          className="readonly-field"
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          Stock Quantity <RequiredStar />
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={variant.stockQuantity}
                          onChange={(e) =>
                            handleVariantChange(
                              idx,
                              "stockQuantity",
                              e.target.value
                            )
                          }
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn-add-variant"
                  onClick={addVariant}
                >
                  + Add Another Variant
                </button>
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSaveProductAtStep3}
                  disabled={loading}
                >
                  {loading ? "Saving Product..." : "Save Product & Next"}
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: IMAGES PER COLOR */}
          {currentStep === 4 && (
            <div className="form-section">
              <h3>Step 4: Upload Images</h3>
              <p className="step-description">
                Upload clear images for each color. We will crop them to a 3:4
                portrait frame, similar to how they appear on the app.
              </p>

              {productColors.length > 0 && (
                <div className="image-status-notice">
                  <strong>Note:</strong> Only product colors from your variants are
                  listed below. Please upload at least one image for every color.
                </div>
              )}

              {completedColors.length > 0 && (
                <div style={{ marginBottom: 12, fontSize: "0.85rem" }}>
                  <strong>Colors completed in this session:</strong>{" "}
                  {completedColors.map((c) => (
                    <span
                      key={c}
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        marginRight: 6,
                        marginTop: 4,
                        borderRadius: 12,
                        border: "1px solid #d1d5db",
                        background: "#f3f4f6",
                      }}
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}

              {productColors.length > 0 ? (
                <div className="form-group" style={{ maxWidth: 320 }}>
                  <label>
                    Image Color <RequiredStar />
                  </label>
                  <select
                    value={selectedImageColor}
                    onChange={(e) => setSelectedImageColor(e.target.value)}
                  >
                    <option value="">Select Color</option>
                    {productColors.map((pc) => (
                      <option key={pc.id} value={pc.colorName}>
                        {pc.colorName}
                      </option>
                    ))}
                  </select>
                  <small className="helper-text">
                    Upload images for one color at a time.
                  </small>
                </div>
              ) : (
                <div className="form-group">
                  <label>Image Color</label>
                  <p style={{ fontSize: "0.85rem", color: "#6b7280" }}>
                    No variant colors found yet. Images will be saved without color
                    grouping.
                  </p>
                </div>
              )}

              <div className="image-views-grid">
                {Object.keys(imageViewTypes).map((viewType) => (
                  <div className="image-view-card" key={viewType}>
                    <h5>{viewType}</h5>

                    <label className="upload-label">
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleImageUploadForView(
                            viewType,
                            e.target.files && e.target.files[0]
                          )
                        }
                      />
                      {!imagePreviewData[viewType] && (
                        <>
                          <div className="upload-icon">📷</div>
                          <div className="upload-text">Click to upload</div>
                        </>
                      )}
                    </label>

                    {imagePreviewData[viewType] && (
                      <div style={{ marginTop: 12, position: "relative" }}>
                        <div className="image-mobile-preview-frame">
                          <img
                            src={imagePreviewData[viewType].previewUrl}
                            alt={viewType}
                          />
                        </div>

                        <div className="image-resolution-info">
                          Original: {imagePreviewData[viewType].originalWidth}×
                          {imagePreviewData[viewType].originalHeight}px
                        </div>

                        {imagePreviewData[viewType].warnings?.length > 0 && (
                          <div className="image-warning-box">
                            <strong>Heads up:</strong>
                            <ul>
                              {imagePreviewData[viewType].warnings.map((w, i) => (
                                <li key={i}>{w}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <button
                          type="button"
                          className="remove-image"
                          onClick={() => removeImageFromView(viewType)}
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleFinish}
                >
                  Finish Product &amp; Close
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading
                    ? "Uploading Images..."
                    : "Save Images for Selected Color"}
                </button>
              </div>

              <p
                style={{
                  marginTop: 8,
                  fontSize: "0.8rem",
                  color: "#6b7280",
                }}
              >
                Tip: After you finish uploading images for all colors, click{" "}
                <strong>"Finish Product &amp; Close"</strong> to exit without
                creating duplicates.
              </p>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
